import { Modal, Button, Group, NumberInput, TextInput, Grid, Autocomplete } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { format } from 'rut.js';

import { useCedeDocumentMutation } from '@api/documents';
import { CommonHash } from '@types';
import { useCompanies } from '@api/companies';
import { useEffect, useMemo } from 'react';
interface CedeDocumentProps {
  opened: boolean;
  close: () => void;
  row: CommonHash;
}

const validDate = (value: string | Date, referenceDate: string, greater: boolean = true) => {
  try {
    const valueDate = new Date(value);
    const referenceDateObj = new Date(referenceDate);

    return greater ? valueDate >= referenceDateObj : valueDate <= referenceDateObj;
  } catch (_e) {
    return false;
  }
};
export const CedeDocumentModal = ({ opened, close, row }: CedeDocumentProps) => {
  const { data: receiversOptions } = useCompanies();
  const { mutate: cedeDocument } = useCedeDocumentMutation();
  const handleCedeDocument = () => {};

  const companies = useMemo(
    () =>
      receiversOptions?.data?.reduce((acc: any, { tax_id, ...rest }: CommonHash) => {
        acc[tax_id] = { tax_id, ...rest };
        return acc;
      }, {}) || {},
    [receiversOptions],
  );

  const onSubmit = (values: any, event: any) => {
    event.preventDefault();
    event.stopPropagation();
    cedeDocument(
      { uuid: row.uuid as string, cession: values },
      {
        onSuccess: () => {
          close();
        },
      },
    );
  };

  const form = useForm({
    initialValues: {
      company_rut: '',
      company_name: '',
      cession_date: new Date(),
      amount: row.total_amount,
      gloss: '',
      payment_date: '',
      contact_name: '',
      contact_phone: '',
      contact_email: '',
    },
    validateInputOnChange: true,
    validateInputOnBlur: true,
    validate: {
      company_rut: (value) => (value === null || value === '' ? 'Este campo es requerido' : null),
      company_name: (value) => (value === null || value === '' ? 'Este campo es requerido' : null),
      cession_date: (value) => {
        if (value === null) return 'Este campo es requerido';
        if (!validDate(value, row.issue_date, true))
          return 'La fecha no puede ser anterior a la fecha del documento';
        if (!validDate(value, new Date().toISOString(), false))
          return 'La fecha no puede ser posterior a hoy';
        return null;
      },
      amount: (value) => {
        if (value === null) return 'Este campo es requerido';
        if (value <= 0) return 'El monto debe ser mayor a 0';
        if (value > row.total_amount) return 'El monto debe ser menor al monto del documento';
        return null;
      },
      gloss: (value) => {
        if (value === null || value === '') return 'Este campo es requerido';
        return null;
      },
      payment_date: (value) => {
        if (value === null || value === '') return 'Este campo es requerido';
        return null;
      },
      // contact_name: (value) => (value === null ? 'Este campo es requerido' : null),
      // contact_phone: (value) => (value === null ? 'Este campo es requerido' : null),
      contact_email: (value) => (value === null || value === '' ? 'Este campo es requerido' : null),
    },
  });

  const onBlurTax = (value: string) => {
    if (value === '') return;
    form.setFieldValue('company_rut', format(value, { dots: false }));
  };

  useEffect(() => {
    const receiver = companies[form.values.company_rut];
    if (receiver) form.setFieldValue('company_name', receiver.legal_name);
  }, [companies, form, form.values.company_rut]);

  return (
    <Modal.Root size="lg" opened={opened} onClose={close} centered>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title fz={20} fw={600}>
            Ceder {row?.dte_type} N° {row?.number}
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={form.onSubmit(onSubmit)}>
            <Grid mb={30}>
              <Grid.Col span={{ base: 12 }}>
                <Autocomplete
                  withAsterisk
                  label="RUT"
                  placeholder="XXXXXXXX-X"
                  {...form.getInputProps('company_rut')}
                  onBlur={(e) => onBlurTax(e.target.value)}
                  data={Object.keys(companies)}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12 }}>
                <TextInput
                  withAsterisk
                  label="Razón Social"
                  placeholder="Empresa SPA"
                  {...form.getInputProps('company_name')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12 }}>
                <DateInput
                  withAsterisk
                  valueFormat="YYYY-MM-DD"
                  label="Fecha de cesión"
                  placeholder="Fecha cesión"
                  maxDate={new Date()}
                  {...form.getInputProps('cession_date')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12 }}>
                <NumberInput
                  withAsterisk
                  label="Monto"
                  placeholder="Monto"
                  allowDecimal={false}
                  decimalSeparator=","
                  thousandSeparator="."
                  {...form.getInputProps('amount')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12 }}>
                <TextInput
                  withAsterisk
                  label="Glosa"
                  placeholder="Glosa"
                  {...form.getInputProps('gloss')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12 }}>
                <DateInput
                  withAsterisk
                  label="Fecha de pago"
                  placeholder="Fecha de pago"
                  valueFormat="YYYY-MM-DD"
                  minDate={new Date()}
                  {...form.getInputProps('payment_date')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12 }}>
                <TextInput
                  label="Nombre del contacto"
                  placeholder="Nombre del contacto"
                  {...form.getInputProps('contact_name')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12 }}>
                <TextInput
                  label="Teléfono del contacto"
                  placeholder="Teléfono del contacto"
                  {...form.getInputProps('contact_phone')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12 }}>
                <TextInput
                  withAsterisk
                  label="Email del contacto"
                  placeholder="Email del contacto"
                  {...form.getInputProps('contact_email')}
                />
              </Grid.Col>
            </Grid>
            <Group justify="end">
              <Button onClick={close}>Cerrar</Button>
              <Button type="submit" onClick={handleCedeDocument}>
                Ceder
              </Button>
            </Group>
          </form>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
